import React, { useCallback, useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';
import PageSlider from 'components/shared/app/PageSlider';
import Config from 'lib/Config';

import { ManagedRequest, RequestStatus } from 'lib/RequestManager';
import {
    ensure7Days, tzDate, last7, today, now, createIsoTimeRange
} from 'utils/DateUtils';
import { getAccountProviders, getAccountTimezone, getGoal, getInnerError, renderError, renderLoader, renderNoDataMessage } from 'lib/utils/adaya/AdayaUtils';
import { combineAllDailyCampaignData, filterAdObjects, calculateMetricsAcrossDays } from 'utils/adaya/ProviderUtils';
import { getProbabilities, getRoasRecommendationCode, last7Metrics } from 'components/pages/Campaigns/utils'
import { getLogger } from 'utils/logging';
import { orZero } from 'utils/NumUtils';
import { ErrorMessages } from 'lib/AppErrors';
import { PageHelper } from 'lib/helpers/PageHelper';
import { ProviderNames } from 'lib/data/models/Providers';
import { Goal } from 'lib/data/models/Goals';
import './style.scss';

const { log, warn } = getLogger('Insights', { color: 'yellow', enabled: false });

const pageHelper = new PageHelper();

export default Insights = (props) => {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(undefined);


    const [insightsMetrics, setInsightsMetrics] = useState(undefined);
    const [dailyMetrics, setDailyMetrics] = useState(undefined);
    const [thisMonthLastYearData, setThisMonthLastYearData] = useState(undefined);
    const [previousMonthLastYearData, setPreviousMonthLastYearData] = useState(undefined);
    const [thisMonthData, setThisMonthData] = useState(undefined);
    const [last3Data, setLast3Data] = useState(undefined);
    const [last7Data, setLast7Data] = useState(undefined);
    const [previous7Data, setPrevious7Data] = useState(undefined);
    const [twoMonthsData, setTwoMonthsData] = useState(undefined);
    const [campaignsData, setCampaignsData] = useState(undefined);

    const [pools, setPools] = useState(undefined);

    const user = props.auth.user;
    const account = user?.activeAccount;

    const providers = getAccountProviders(account, [ProviderNames.ADAYA]);
    const timezone = getAccountTimezone(account);

    useEffect(async () => {
        setLoading(true);
        await fetchAllData();
    }, [user]);

    const fetchAllData = async () => {
        if (!account) return;
        try {
            await Promise.all([
                fetchData('last3', { timeRange: createIsoTimeRange('last3days') }),
                fetchData('last7', { timeRange: createIsoTimeRange('last7days') }),
                fetchData('previous7', { timeRange: createIsoTimeRange('previous7days') }),
                fetchData('thisMonth', { timeRange: createIsoTimeRange('thisMonth') }),
                fetchData('thisMonthLastYear', { timeRange: createIsoTimeRange('thisMonthLastYear') }),
                fetchData('previousMonthLastYear', { timeRange: createIsoTimeRange('previousMonthLastYear') }),
                fetchData('twoMonths', { timeRange: createIsoTimeRange('twoMonths') })
            ])
        } catch (e) {
            setError(getInnerError(e));
        }
        setLoading(false);
    }

    const fetchData = async (id, filter) => {
        if (!account) return;
        log(`fetchData`, id, filter);
        try {
            filter.account = account;
            filter.providers = providers.map(p => p.name);
            let r = await pageHelper.getDailyCampaignDataRequest(filter);
            onDataResponse(r, filter, id);
        } catch (e) {
            setError(getInnerError(e));
        }
    }

    const onDataResponse = (r: ManagedRequest, filter, id) => {
        try {
            log('onDataResponse', id, r);

            if (r.status == RequestStatus.ERROR) {
                // error loading data, it should show notifications
                return setError(getInnerError(r));
            }

            if (r.status == RequestStatus.FINISHED) {
                if (id == 'last3') {
                    const metrics = calculateMetricsAcrossDays(r.response);
                    setLast3Data(metrics);
                } else if (id == 'last7') {
                    const metrics = calculateMetricsAcrossDays(r.response);
                    setLast7Data(metrics);
                } else if (id == 'previous7') {
                    const metrics = calculateMetricsAcrossDays(r.response);
                    setPrevious7Data(metrics);
                } else if (id == 'thisMonth') {
                    const metrics = calculateMetricsAcrossDays(r.response);
                    setThisMonthData(metrics);
                } else if (id == 'thisMonthLastYear') {
                    const days = Object.keys(r.response);
                    const data = days.map(day => ({
                        date: day,
                        totals: r.response[day].getData().totals
                    }));
                    setThisMonthLastYearData(data);
                } else if (id == 'previousMonthLastYear') {
                    const days = Object.keys(r.response);
                    const data = days.map(day => ({
                        date: day,
                        totals: r.response[day].getData().totals
                    }));
                    setPreviousMonthLastYearData(data);
                } else if (id == 'twoMonths') {
                    const days = Object.keys(r.response);
                    const data = days.map(day => ({
                        date: day,
                        totals: r.response[day].getData().totals
                    }));
                    setTwoMonthsData(data);
                }
                return;
            }

            setError(getInnerError(r));
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    }

    useEffect(() => {
        if (thisMonthData && user && account) {
            const _today = today();
            const daysInMonth = _today.daysInMonth();
            const thisMonthSoFarLength = _today.diff(today().startOf('month'), 'days') + 1;

            const accountAgeDays = props.auth.getAccountAge();
            const productsGoal = getGoal(account, Goal.Products);
            const revenueGoal = getGoal(account, Goal.Revenue);
            const cppGoal = getGoal(account, Goal.Cpp);
            const aovGoal = getGoal(account, Goal.Aov);
            const roasGoal = getGoal(account, Goal.Roas);
            const adspendGoal = getGoal(account, Goal.Adspend);

            const monthProjectedRevenue = orZero((thisMonthData.revenue / thisMonthSoFarLength) * daysInMonth);
            const actualRoasPercOfGoal = orZero(((thisMonthData.roas - roasGoal) / roasGoal) * 100);
            const actualCppPercOfGoal = orZero(((thisMonthData.cpp - cppGoal) / cppGoal) * 100);
            const actualAovPercOfGoal = orZero(((thisMonthData.aov - aovGoal) / aovGoal) * 100);
            const projectedRevenuePercOfGoal = orZero(((monthProjectedRevenue - revenueGoal) / monthProjectedRevenue) * 100);
            const purchasesGoal = (aovGoal == 0) ? 0 : orZero((revenueGoal / aovGoal).toFixed(2));

            const dailyRevenueGoal = revenueGoal / daysInMonth;
            const dailyPurchasesGoal = purchasesGoal / daysInMonth;
            const dailyAdspendGoal = adspendGoal / daysInMonth;
            const dailyProductsGoal = productsGoal / daysInMonth;
            const dailyAovGoal = aovGoal;
            const dailyCppGoal = cppGoal;
            const dailyRoasGoal = roasGoal;

            setDailyMetrics({
                revenueGoal: dailyRevenueGoal,
                purchasesGoal: dailyPurchasesGoal,
                adspendGoal: dailyAdspendGoal,
                productsGoal: dailyProductsGoal,
                aovGoal: dailyAovGoal,
                cppGoal: dailyCppGoal,
                roasGoal: dailyRoasGoal
            });

            setInsightsMetrics({
                accountAgeDays,
                revenueGoal,
                roasGoal,
                adspendGoal,
                projectedRevenuePercOfGoal,
                actualRoasPercOfGoal,
                actualCppPercOfGoal,
                actualAovPercOfGoal
            });
        }
    }, [thisMonthData, props.auth.user]);

    useEffect(() => {
        if (campaignsData && insightsMetrics) {
            let combinedData = combineAllDailyCampaignData(campaignsData);
            combinedData = filterAdObjects(combinedData, (ao, aoType) => ao.totals?.adspend > 0, true);

            const data = combinedData.campaigns.map(c => {
                const [probDecRoas, probIncRoas] = getProbabilities(last7Metrics(campaignsData, c, 'ROAS'));
                const roasRecommendationCode = getRoasRecommendationCode(c, insightsMetrics.roasGoal, probDecRoas, probIncRoas);
                return {
                    campaign: c,
                    roasRecommendationCode
                };
            });
            setCampaigns(data);
        }
    }, [campaignsData, insightsMetrics]);

    const sumDataSetTotals = (dataDays) => {
        let totals: any = {};
        totals.adspend = totals.revenue = totals.items = totals.orders = totals.cpp = totals.aov = totals.roas = 0;

        dataDays.forEach(d => {
            totals.adspend += d.totals.adspend || 0;
            totals.revenue += d.totals.revenue || 0;
            totals.items += d.totals.items || 0;
            totals.orders += d.totals.orders || 0;
            totals.cpp += d.totals.cpp || 0;
            totals.aov += d.totals.aov || 0;
            totals.roas += d.totals.roas || 0;
        });

        // go through account days and discount non real-days:
        let actualDays = dataDays.length;
        let _today = today();
        dataDays.forEach(d => {
            let md = tzDate(d.date);
            if (md.isAfter(_today)) {
                actualDays--;
            }
        });

        // averages
        totals.ordersAvg = orZero(totals.orders / actualDays);
        totals.itemsPerDayAvg = orZero(totals.items / actualDays);
        totals.revenueAvg = orZero(totals.revenue / actualDays);
        totals.adspendAvg = orZero(totals.adspend / actualDays);
        totals.roas = orZero(totals.revenue / totals.adspend);
        totals.cpp = orZero(totals.adspend / totals.orders);
        totals.aov = orZero(totals.revenue / totals.orders);

        return totals;
    }

    const lastMonthSample = () => {
        const _last7Time = last7();
        const _lastMonthSampleDays = ensure7Days(
            twoMonthsData.filter(item => {
                return now().set(item.date).isSameOrAfter(_last7Time[0]) &&
                    now().set(item.date).isSameOrBefore(_last7Time[1]);
            })
        );

        let _lastMonthSample = {
            days: _lastMonthSampleDays,
            totals: sumDataSetTotals(_lastMonthSampleDays)
        };
        return _lastMonthSample;
    };

    const lastYearSample = () => {
        const _lastYearSampleDays = ensure7Days(
            thisMonthLastYearData.concat(previousMonthLastYearData).filter(item => {
                return now().set(item.date).isSameOrBefore(now().subtract(1, 'years').subtract(1, 'days').endOf('day')) &&
                    now().set(item.date).isSameOrAfter(now().subtract(1, 'years').subtract(9, 'days').startOf('day'))
            })
        );

        const _lastYearSample = {
            days: _lastYearSampleDays,
            totals: sumDataSetTotals(_lastYearSampleDays)
        };
        return _lastYearSample;
    };

    useEffect(() => {
        if (insightsMetrics && dailyMetrics && last3Data && last7Data && thisMonthData && previous7Data && thisMonthLastYearData && previousMonthLastYearData && twoMonthsData) {
            const revenueGoal = insightsMetrics.revenueGoal;
            const roasGoal = insightsMetrics.roasGoal;
            const cppGoal = insightsMetrics.cppGoal;
            const aovGoal = insightsMetrics.aovGoal;

            const dailyPurchasesGoal = dailyMetrics.purchasesGoal;
            const dailyRevenueGoal = dailyMetrics.revenueGoal;
            const dailyAdspendGoal = dailyMetrics.adspendGoal;

            const actualRevenuePercOfGoal = orZero(((thisMonthData.revenue - revenueGoal) / revenueGoal) * 100);
            const actualRoasPercOfGoal = orZero(((thisMonthData.roas - roasGoal) / roasGoal) * 100);

            const last3PurchasePercOfGoal = orZero(((last3Data.ordersAvg - dailyPurchasesGoal) / dailyPurchasesGoal) * 100);
            const last3RevenuePercOfGoal = orZero(((last3Data.revenueAvg - dailyRevenueGoal) / dailyRevenueGoal) * 100);
            const last3AdspendPercOfGoal = orZero(((last3Data.adspendAvg - dailyAdspendGoal) / dailyAdspendGoal) * 100);
            const last3RoasPercOfGoal = orZero(((last3Data.roas - roasGoal) / roasGoal) * 100);

            const last3AovPercOfGoal = orZero(((last3Data.aov - aovGoal) / aovGoal) * 100);
            const last3RevenueGroup = [last3PurchasePercOfGoal, last3AdspendPercOfGoal, last3AovPercOfGoal];
            const last3CppPercOfGoal = orZero(((last3Data.cpp - cppGoal) / cppGoal) * 100);
            const last3RoasGroup = [last3CppPercOfGoal, last3AovPercOfGoal];

            const closestLast3Revenue = last3RevenueGroup.reduce((a, b) => {
                return Math.abs(b - actualRevenuePercOfGoal) < Math.abs(a - actualRevenuePercOfGoal) ? b : a;
            })
            const closestLast3Roas = last3RoasGroup.reduce((a, b) => {
                return Math.abs(b - actualRoasPercOfGoal) < Math.abs(a - actualRoasPercOfGoal) ? b : a;
            })

            const last3RevenueWhyMetric = last3RevenueGroup.indexOf(closestLast3Revenue);
            const last3RoasWhyMetric = last3RoasGroup.indexOf(closestLast3Roas);

            const last7PurchasePercOfGoal = orZero(((last7Data.orders - dailyPurchasesGoal) / dailyPurchasesGoal) * 100);
            const last7RevenuePercOfGoal = orZero(((last7Data.revenueAvg - dailyRevenueGoal) / dailyRevenueGoal) * 100);
            const last7RoasPercOfGoal = roasGoal == 0 ? 0 : (((last7Data.roas - roasGoal) / roasGoal) * 100);
            const last7AdspendPercOfGoal = orZero(((last7Data.adspend - dailyAdspendGoal) / dailyAdspendGoal) * 100);

            const last7AovPercOfGoal = orZero(((last7Data.AovAvg - aovGoal) / aovGoal) * 100)
            const last7RevenueGroup = [last7PurchasePercOfGoal, last7AdspendPercOfGoal, last7AovPercOfGoal];
            const last7CppPercOfGoal = orZero(((last7Data.CppAvg - cppGoal) / cppGoal) * 100);
            const last7RoasGroup = [last7CppPercOfGoal, last7AovPercOfGoal];

            const closestLast7Revenue = last7RevenueGroup.reduce((a, b) => {
                return Math.abs(b - actualRevenuePercOfGoal) < Math.abs(a - actualRevenuePercOfGoal) ? b : a;
            });
            const closestLast7Roas = last7RoasGroup.reduce((a, b) => {
                return Math.abs(b - actualRoasPercOfGoal) < Math.abs(a - actualRoasPercOfGoal) ? b : a;
            });

            const last7RevenueWhyMetric = last7RevenueGroup.indexOf(closestLast7Revenue);
            const last7RoasWhyMetric = last7RoasGroup.indexOf(closestLast7Roas);

            const previous7PurchasePerc = orZero(((last7Data.orders - previous7Data.ordersAvg) / previous7Data.ordersAvg) * 100);
            const previous7AdspendPerc = orZero(((last7Data.adspend - previous7Data.adspendAvg) / previous7Data.adspendAvg) * 100);

            const previous7AovPerc = orZero(((last7Data.aov - previous7Data.aov) / previous7Data.aov) * 100);
            const previous7RevenueGroup = [previous7PurchasePerc, previous7AdspendPerc, previous7AovPerc];
            const previous7CppPerc = orZero(((last7Data.cpp - previous7Data.cpp) / previous7Data.cpp) * 100);
            const previous7RoasGroup = [previous7CppPerc, previous7AovPerc];

            const _lastMonthSample = lastMonthSample();

            const last7Previous7Revenue = orZero(((last7Data.revenueAvg - previous7Data.revenueAvg) / previous7Data.revenueAvg) * 100);
            const last7Previous7Roas = orZero(((last7Data.roas - previous7Data.roas) / previous7Data.roas) * 100);
            const last7LastMonthRevenue = orZero(((last7Data.revenueAvg - _lastMonthSample.totals.revenueAvg) / _lastMonthSample.totals.revenueAvg) * 100);
            const last7LastMonthRoas = orZero(((last7Data.roas - _lastMonthSample.totals.roas) / _lastMonthSample.totals.roas) * 100);

            const closestPrevious7Revenue = previous7RevenueGroup.reduce((a, b) => {
                return Math.abs(b - last7Previous7Revenue) < Math.abs(a - last7Previous7Revenue) ? b : a;
            })
            const closestPrevious7Roas = previous7RoasGroup.reduce((a, b) => {
                return Math.abs(b - last7Previous7Roas) < Math.abs(a - last7Previous7Roas) ? b : a;
            })

            const previous7RevenueWhyMetric = previous7RevenueGroup.indexOf(closestPrevious7Revenue);
            const previous7RoasWhyMetric = previous7RoasGroup.indexOf(closestPrevious7Roas);

            const lastMonthPurchasePerc = orZero(((last7Data.orders - _lastMonthSample.totals.ordersAvg) / _lastMonthSample.totals.ordersAvg) * 100);
            const lastMonthAdspendPerc = orZero(((last7Data.adspend - _lastMonthSample.totals.adspendAvg) / _lastMonthSample.totals.adspendAvg) * 100);

            const lastMonthAovPerc = orZero(((last7Data.aov - _lastMonthSample.totals.aov) / _lastMonthSample.totals.aov) * 100);
            const lastMonthRevenueGroup = [lastMonthPurchasePerc, lastMonthAdspendPerc, lastMonthAovPerc];
            const lastMonthCppPerc = orZero(((last7Data.cpp - _lastMonthSample.totals.cpp) / _lastMonthSample.totals.cpp) * 100);
            const lastMonthRoasGroup = [lastMonthCppPerc, lastMonthAovPerc];

            const closestLastMonthRevenue = lastMonthRevenueGroup.reduce((a, b) => {
                return Math.abs(b - last7LastMonthRevenue) < Math.abs(a - last7LastMonthRevenue) ? b : a;
            })
            const closestLastMonthRoas = lastMonthRoasGroup.reduce((a, b) => {
                return Math.abs(b - last7LastMonthRoas) < Math.abs(a - last7LastMonthRoas) ? b : a;
            })

            const lastMonthRevenueWhyMetric = lastMonthRevenueGroup.indexOf(closestLastMonthRevenue);
            const lastMonthRoasWhyMetric = lastMonthRoasGroup.indexOf(closestLastMonthRoas);

            const _lastYearSample = lastYearSample();

            const last7LastYearRevenue = orZero(((last7Data.revenueAvg - _lastYearSample.totals.revenueAvg) / _lastYearSample.totals.revenueAvg) * 100);
            const last7LastYearRoas = orZero(((last7Data.roas - _lastYearSample.totals.roas) / _lastYearSample.totals.roas) * 100);

            const lastYearPurchasePerc = orZero(((last7Data.orders - _lastYearSample.totals.ordersAvg) / _lastYearSample.totals.ordersAvg) * 100);
            const lastYearAdspendPerc = orZero(((last7Data.adspend - _lastYearSample.totals.adspendAvg) / _lastYearSample.totals.adspendAvg) * 100);
            const lastYearAovPerc = orZero(((last7Data.aov - _lastYearSample.totals.aov) / _lastYearSample.totals.aov) * 100);
            const lastYearCppPerc = orZero(((last7Data.cpp - _lastYearSample.totals.cpp) / _lastYearSample.totals.cpp) * 100);

            const lastYearRevenueGroup = [lastYearPurchasePerc, lastYearAdspendPerc, lastYearAovPerc];
            const lastYearRoasGroup = [lastYearCppPerc, lastYearAovPerc];

            const closestLastYearRevenue = lastYearRevenueGroup.reduce((a, b) => {
                return Math.abs(b - last7LastYearRevenue) < Math.abs(a - last7LastYearRevenue) ? b : a;
            })
            const closestLastYearRoas = lastYearRoasGroup.reduce((a, b) => {
                return Math.abs(b - last7LastYearRoas) < Math.abs(a - last7LastYearRoas) ? b : a;
            })

            const lastYearRevenueWhyMetric = lastYearRevenueGroup.indexOf(closestLastYearRevenue);
            const lastYearRoasWhyMetric = lastYearRoasGroup.indexOf(closestLastYearRoas);

            const pools = [
                {
                    time: '3 days',
                    type: 'Revenue',
                    comp: last3RevenuePercOfGoal,
                    selector: last3RevenueGroup,
                    index: last3RevenueWhyMetric,
                    comparison: 'goal',
                    weight: 8.5,
                },
                {
                    time: '3 days',
                    type: 'ROAS',
                    comp: last3RoasPercOfGoal,
                    selector: last3RoasGroup,
                    index: last3RoasWhyMetric,
                    comparison: 'goal',
                    weight: 8.6,
                },
                {
                    time: '7 days',
                    type: 'Revenue',
                    comp: last7RevenuePercOfGoal,
                    selector: last7RevenueGroup,
                    index: last7RevenueWhyMetric,
                    comparison: 'goal',
                    weight: 9.4,
                },
                {
                    time: '7 days',
                    type: 'ROAS',
                    comp: last7RoasPercOfGoal,
                    selector: last7RoasGroup,
                    index: last7RoasWhyMetric,
                    comparison: 'goal',
                    weight: 9.6,
                },
                {
                    time: '7 days',
                    type: 'Revenue',
                    comp: last7Previous7Revenue,
                    selector: previous7RevenueGroup,
                    index: previous7RevenueWhyMetric,
                    comparison: 'the previous 7 days',
                    weight: 7.7,
                },
                {
                    time: '7 days',
                    type: 'ROAS',
                    comp: last7Previous7Roas,
                    selector: previous7RoasGroup,
                    index: previous7RoasWhyMetric,
                    comparison: 'the previous 7 days',
                    weight: 7.8,
                },
                {
                    time: '7 days',
                    type: 'Revenue',
                    comp: last7LastMonthRevenue,
                    selector: lastMonthRevenueGroup,
                    index: lastMonthRevenueWhyMetric,
                    comparison: 'this time last month',
                    weight: 3.8,
                },
                {
                    time: '7 days',
                    type: 'ROAS',
                    comp: last7LastMonthRoas,
                    selector: lastMonthRoasGroup,
                    index: lastMonthRoasWhyMetric,
                    comparison: 'this time last month',
                    weight: 3.9
                },
                {
                    time: '7 days',
                    type: 'Revenue',
                    comp: last7LastYearRevenue,
                    selector: lastYearRevenueGroup,
                    index: lastYearRevenueWhyMetric,
                    comparison: 'this time last year',
                    weight: 2.2,
                },
                {
                    time: '7 days',
                    type: 'ROAS',
                    comp: last7LastYearRoas,
                    selector: lastYearRoasGroup,
                    index: lastYearRoasWhyMetric,
                    comparison: 'this time last year',
                    weight: 2.1
                },
            ];

            setPools(pools);
        }
    }, [thisMonthData, last3Data, last7Data, insightsMetrics, dailyMetrics, previous7Data, thisMonthLastYearData, previousMonthLastYearData, twoMonthsData]);

    const getMetricWeight = (perc) => {
        if (perc < -33) {
            return 6;
        } else if (perc >= 33) {
            return 12;
        } else if (perc < -10 && perc > -33) {
            return 4;
        } else if (perc > -10 && perc < 0) {
            return 6;
        } else if (perc > 10 && perc < 33) {
            return 10;
        } else if (perc < 10 && perc >= 0) {
            return 8;
        } else {
            return 0;
        }
    };

    const getWhyMetric = (index, type) => {
        if (type === 'Revenue') {
            switch (index) {
                case 0:
                    return 'Purchases';
                case 1:
                    return 'Adspend';
                case 2:
                    return 'AOV';
            }
        } else {
            switch (index) {
                case 0:
                    return 'CPP';
                case 1:
                    return 'AOV';
            }
        }
    };

    const renderTrends = () => {
        let message = '';

        if (insightsMetrics && insightsMetrics.accountAgeDays < 8) {
            let dr = 8 - insightsMetrics.accountAgeDays;
            message = `We're analyzing your campaign data and will have trends available in ${dr} day${dr == 1 ? '' : 's'}.`;
        } else if (pools) {
            const now = new Date();
            const dayOfWeek = now.getDay();

            const strengthMessage = (comp) => {
                if (comp < 0) {
                    return 'weak';
                } else if (comp < .5) {
                    return 'strong';
                } else {
                    return 'really strong';
                }
            };

            const completedPools = pools ? pools.map(p => {
                return {
                    message: `The last ${p.time}, <b>${p.type} was ${p.comp < 0 ? 'below' : 'above'} ${p.comparison === 'goal' ? 'your goal' : `${p.comparison}`} by ${Math.floor(Math.abs(p.comp))}%,</b> <span class="break"/>mostly because ${getWhyMetric(p.index, p.type)} was ${strengthMessage(p.comp)}.`,
                    weight: (p.weight + getMetricWeight(p.selector[p.index])) / 2,
                    comparison: p.comparison,
                    comp: p.comp,
                    time: p.time,
                    //color: p.comp < 0 ? 'yellow' : 'green',
                };
            }).sort((a, b) => {
                return a.weight < b.weight ? 1 : a.weight > b.weight ? -1 : 0;
            }) : [];
            const selected = completedPools[dayOfWeek % completedPools.length];
            if (selected) {
                message = selected.message;
            }
        }

        return (
            <div className="insight-container swiper-slide trends">
                <h1>Trending</h1>
                {<div className="messaging" dangerouslySetInnerHTML={{ __html: message }}></div>}
            </div>
        );
    };

    const renderPace = () => {
        let message = '';

        if (insightsMetrics) {
            const projectedRevenuePercOfGoal = insightsMetrics.projectedRevenuePercOfGoal;
            const actualRoasPercOfGoal = insightsMetrics.actualRoasPercOfGoal;
            const actualCppPercOfGoal = insightsMetrics.actualCppPercOfGoal;
            const actualAovPercOfGoal = insightsMetrics.actualAovPercOfGoal;
            if (projectedRevenuePercOfGoal < -10 && actualRoasPercOfGoal >= -20) {
                message = 'ROAS is on track but Revenue is <b>below your goal pace for the month.</b><br/><br/>We recommend increasing Adspend by using vertical or automated scaling for any winning campaigns and horizontal scaling (launching new campaigns with winning ad creative to new audiences.)';
            } else if (projectedRevenuePercOfGoal >= -10 && actualRoasPercOfGoal >= -20) {
                message = 'You’re still on track for the month!<br/><br/><b>Revenue and ROAS both look good.</b><br/><br/>Continue to optimize any underperforming campaigns and scale winning campaigns. Launch new campaigns to keep adspend up. Nice work!';
            } else if (projectedRevenuePercOfGoal >= -10 && actualRoasPercOfGoal < -20 && actualCppPercOfGoal <= 20 && actualAovPercOfGoal < -20) {
                message = 'Revenue is still on track!<br/><br/><b>ROAS is below your target, mostly due to a lower AOV.</b><br/><br/>We recommend working on ways to increase your overall AOV this month to boost ROAS – common ways are bundles, tiered offers, and promoting higher priced products in your campaigns.';
            } else if (projectedRevenuePercOfGoal >= -10 && actualRoasPercOfGoal < -20 && actualCppPercOfGoal > 20 && actualAovPercOfGoal >= -20) {
                message = 'Revenue is still on track!<br/><br/><b>ROAS is below your target, mostly due to a higher Cost Per Purchase.</b><br/><br/>We recommend toggling off campaigns with the highest CPP to bring your overall ROAS up – while also scaling up your winning campaigns, both vertically and horizontally.';
            } else if (projectedRevenuePercOfGoal >= -10 && actualRoasPercOfGoal < -20 && actualCppPercOfGoal > 20 && actualAovPercOfGoal < -20) {
                message = 'Revenue is still on track!<br/><br/><b>But ROAS is below your target, mostly due to the combination of a higher Cost Per Purchase and lower AOV.</b><br/><br/>We recommend identifying the ad creative with the highest ROAS and the products with the highest AOV.<br/><br/>Continue to launch new campaigns with your high-performing ad elements to new audiences. It can also help to test new campaign structures, using proven ad creative and audiences.';
            } else if (projectedRevenuePercOfGoal >= -10 && actualRoasPercOfGoal < -20 &&
                (actualCppPercOfGoal > 0 && actualCppPercOfGoal < 20) &&
                (actualAovPercOfGoal < 0 && actualAovPercOfGoal > -20)) {
                message = 'Revenue is still on track!<br/><br/><b>But ROAS is below your goal, due to a slightly higher Cost Per Purchase and a slightly lower AOV.</b><br/><br/>We recommend toggling off campaigns with the highest CPP to bring your CPP down and your overall ROAS up. To increase AOV, consider a tiered or bundled offer to increase cart size.';
            } else if (projectedRevenuePercOfGoal < -10 && actualRoasPercOfGoal < -20) {
                message = '<b>Revenue and ROAS are both currently off your target pace</b> for the month.<br/><br/>If your current ROAS is acceptable, then you can increase Adspend to boost Revenue – by using vertical or automated scaling for any winning campaigns and horizontal scaling (launching new campaigns with winning ad creative to new audiences.)<br/><br/><b>To boost ROAS immediately</b>, we recommend toggling off campaigns with the lowest ROAS and use horizontal scaling to launch new campaigns.';
            }
        }

        return (
            <div className="insight-container swiper-slide pace">
                <h1>Current Pace</h1>
                <div className={`messaging`} dangerouslySetInnerHTML={{ __html: message }}></div>
            </div>
        );
    };

    const renderAction = () => {
        let message = '';

        if (insightsMetrics && insightsMetrics.accountAgeDays < 8) {
            message = `We’re monitoring your campaigns for you, nothing to address at this time.`;
        } else if (campaigns) {
            const scaleUps = campaigns.filter(x => x.roasRecommendationCode > 0).length;
            const monitors = campaigns.filter(x => x.roasRecommendationCode == 0).length;
            const toggleOffs = campaigns.filter(x => x.roasRecommendationCode < 0).length;

            const link = (code, words) => {
                return (
                    <Link to={{ pathname: '/campaigns', state: { recCode: code } }}>
                        <b>{words}</b>
                    </Link>
                );
            };

            if (scaleUps > 0 && toggleOffs > 0 && monitors > 0) {
                message = <>There are {scaleUps} campaigns that are qualified to {link(1, 'scale up')} and {toggleOffs} campaigns that are recommended to {link(-1, 'optimize.')}<br /><br />We'll continue monitoring the other {monitors} for you.</>;
            } else if (scaleUps > 0 && toggleOffs == 0 && monitors > 0) {
                message = <>There are {scaleUps} campaigns that are qualified to {link(1, 'scale up!')}<br /><br />We're monitoring the other {monitors} campaigns for you.</>;
            } else if (scaleUps > 0 && toggleOffs > 0 && monitors == 0) {
                message = <>There are currently {scaleUps} campaigns that are qualified to {link(1, 'scale up')} and {toggleOffs} campaigns recommended to {link(-1, 'optimize.')}</>;
            } else if (scaleUps > 0 && toggleOffs == 0 && monitors == 0) {
                message = <>There are currently {scaleUps} campaigns that are qualified to {link(1, 'scale up!')}<br /><br />Nothing else to do at this time.</>;
            } else if (scaleUps == 0 && toggleOffs == 0 && monitors > 0) {
                message = <>We're monitoring your campaigns for you, {link(0, 'nothing to address at this time.')}</>;
            } else if (scaleUps == 0 && toggleOffs > 0 && monitors == 0) {
                message = <>Currently there are {toggleOffs} campaigns recommended to {link(-1, 'optimize.')}</>;
            } else if (scaleUps == 0 && toggleOffs > 0 && monitors > 0) {
                message = <>Currently there are {toggleOffs} campaigns recommended to go ahead and {link(-1, 'optimize.')}<br /><br />That’s it for now, we’ll continue monitoring the other {monitors} for you.</>;
            } else if (scaleUps == 0 && toggleOffs == 0 && monitors == 0) {
                message = <>There are no campaign recommendations at this time.</>;
            }
        }

        return (
            <div className="insight-container swiper-slide action dark">
                <h1>Action Items</h1>
                <div className={`messaging`}>
                    {<>{message}</>}
                </div>
            </div>
        );
    };

    return (
        <div className={'insights-page page-layout ' + (error ? ' has-error' : '')}>

            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Insights</title></Helmet>

            {loading && renderLoader(true)}

            {!loading && (
                <>
                {error && renderError(ErrorMessages.defaultErrorMessage(), error, "large light")}
                {!error && (
                    <PageSlider id="insight-slider">
                        {renderTrends()}
                        {renderPace()}
                        {renderAction()}
                    </PageSlider>
                )}
                </>
            )}
        </div>
    );

};